import { useEffect, useMemo, useState } from "react";
import { PlaneLanding, Check, Loader2, Info } from "lucide-react";
import { api } from "@/lib/api";
import HUDFrame from "@/components/HUDFrame";

const verdictTone = (v) =>
  v === "green"
    ? "text-signalGreen border-signalGreen/50 bg-signalGreen/5"
    : v === "red"
      ? "text-signalRed border-signalRed/50 bg-signalRed/5"
      : "text-signalAmber border-signalAmber/50 bg-signalAmber/5";

const deltaColor = (n, invert = false) => {
  if (n === 0) return "text-mist";
  const good = invert ? n > 0 : n < 0;
  return good ? "text-signalGreen" : "text-signalAmber";
};

export default function Runway() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/runway")
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setSelectedId(r.data.scenarios?.[0]?.scenario?.scenario_id || null);
      })
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Runway unavailable."));
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(
    () => data?.scenarios?.find((a) => a.scenario.scenario_id === selectedId) || null,
    [data, selectedId]
  );

  if (err) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-mist" data-testid="runway-error">
        {String(err)}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-16 font-mono text-[11px] uppercase tracking-[0.22em] text-mist" data-testid="runway-loading">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        Preparing landing assessments…
      </div>
    );
  }

  const s = data.signals;

  return (
    <div data-testid="runway-page" className="mx-auto max-w-7xl px-6 py-10 md:px-8 md:py-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
        R-03 · Runway
      </p>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
        Can this fit?
      </h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-mist">
        Landing assessments computed from your household profile. Each scenario
        shows how a possible commitment would land against your capacity,
        compression, and recovery.
      </p>

      {/* current state pill */}
      <div className="mt-8 flex flex-wrap gap-3 border-t border-rule pt-6 font-mono text-[10px] uppercase tracking-[0.22em]">
        <span className="text-mist">Current state:</span>
        <span>
          Capacity <span className="text-ink">{s.capacity_load}%</span>
        </span>
        <span className="text-mist">·</span>
        <span>
          Compression <span className="text-ink">{s.compression_index}%</span>
        </span>
        <span className="text-mist">·</span>
        <span>
          Recovery <span className="text-ink">{s.recovery_margin}%</span>
        </span>
      </div>

      <div className="mt-8 grid gap-8 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Scenarios generated from your profile
          </div>
          <div className="mt-4 space-y-3">
            {data.scenarios.map((a) => (
              <button
                key={a.scenario.scenario_id}
                onClick={() => setSelectedId(a.scenario.scenario_id)}
                data-testid={`runway-scenario-${a.scenario.scenario_id}`}
                className={`flex w-full items-start justify-between gap-4 border px-5 py-4 text-left transition-colors duration-300 ${
                  selectedId === a.scenario.scenario_id
                    ? "border-teal bg-deep/60"
                    : "border-rule bg-deep/30 hover:border-teal/50"
                }`}
              >
                <div className="flex-1">
                  <div className="text-sm text-ink">{a.scenario.title}</div>
                  <div className="mt-1 text-xs text-mist">{a.scenario.description}</div>
                </div>
                <span
                  className={`whitespace-nowrap border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${verdictTone(a.verdict)}`}
                >
                  {a.verdict}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-7">
          {selected && (
            <HUDFrame className="bg-deep/40 p-8" tone={selected.verdict === "red" ? "amber" : "teal"}>
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
                <PlaneLanding className="h-3.5 w-3.5" strokeWidth={1.5} />
                Landing assessment
              </div>

              <h2 className="mt-4 font-display text-2xl text-ink md:text-3xl" data-testid="runway-selected-title">
                {selected.scenario.title}
              </h2>
              <p className="mt-2 text-sm text-mist">{selected.scenario.description}</p>

              {/* deltas */}
              <div className="mt-8 grid grid-cols-3 gap-6 border-y border-rule py-6" data-testid="runway-deltas">
                {[
                  ["Capacity", s.capacity_load, selected.new_capacity, selected.capacity_delta],
                  ["Compression", s.compression_index, selected.new_compression, selected.compression_delta],
                  ["Recovery", s.recovery_margin, selected.new_recovery, selected.recovery_delta],
                ].map(([k, before, after, delta]) => (
                  <div key={k}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                      {k}
                    </div>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="font-mono text-2xl text-ink">{after}%</span>
                      <span
                        className={`font-mono text-xs ${deltaColor(delta, k === "Recovery")}`}
                      >
                        {delta > 0 ? "+" : ""}
                        {delta}
                      </span>
                    </div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
                      was {before}%
                    </div>
                  </div>
                ))}
              </div>

              {/* verdict + mitigation */}
              <div className="mt-6 flex items-start gap-3">
                <span
                  className={`mt-1 h-2 w-2 flex-shrink-0 ${
                    selected.verdict === "green"
                      ? "bg-signalGreen"
                      : selected.verdict === "red"
                        ? "bg-signalRed"
                        : "bg-signalAmber"
                  }`}
                />
                <div>
                  <div
                    className={`font-mono text-[11px] uppercase tracking-[0.22em] ${verdictTone(selected.verdict).split(" ")[0]}`}
                    data-testid="runway-verdict"
                  >
                    {selected.verdict === "green"
                      ? "Cleared for landing"
                      : selected.verdict === "red"
                        ? "Do not attempt without offload"
                        : "Land with mitigation"}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-ink">
                    {selected.mitigation}
                  </p>
                  <ul className="mt-3 space-y-1 text-xs text-mist">
                    {selected.reasoning.map((r, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Info className="mt-0.5 h-3 w-3 text-mist/70" strokeWidth={1.5} />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 flex gap-3">
                <button
                  data-testid="runway-accept-btn"
                  className="flex items-center gap-2 bg-teal px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover"
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {selected.verdict === "green" ? "Land it" : "Accept mitigation"}
                </button>
                <button className="border border-rule px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-amber hover:text-ink">
                  Defer
                </button>
              </div>
            </HUDFrame>
          )}
        </div>
      </div>
    </div>
  );
}
